import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchPteCoreListeningTest, submitPteCoreListeningSST } from '../../api';
import "./ptecorereading.css";
import PteCoreListeningScore, { ListeningResult } from './PteCoreListeningScore.tsx';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';


const getStoredUser = (reduxUser: any) => {
    if (reduxUser?.result) return reduxUser.result;
    try {
        const localUser = JSON.parse(localStorage.getItem("user_info") as string);
        if (localUser?.result) return localUser.result;
    } catch { }
    return null;
};

const getAssetUrl = (path: string) => {
    if (!path) return "";
    if (path.startsWith("http") || path.startsWith("data:")) return path;
    const cleanPath = path.startsWith("/") ? path.slice(1) : path;
    // If it has a path separator, assume full path is provided (e.g. uploads/foo.mp3)
    if (cleanPath.includes("/")) return `${API_BASE_URL}/${cleanPath}`;
    // Otherwise assume it's a GridFS file served via /image/
    return `${API_BASE_URL}/image/${cleanPath}`;
};

// --- Helper Functions (Copied from Reading Test) ---

const normalizeText = (s: string = "") => ("" + s).trim().toLowerCase();

function scoreMCMA(question: ListeningQuestion, userSelection: string[] = []) {
    // Extract correct answers from Q1_Answer (common DB field)
    let correctAnswers: string[] = [];
    const rawAns = question.Q1_Answer || question.Answer;

    if (Array.isArray(rawAns)) {
        correctAnswers = rawAns;
    } else if (typeof rawAns === "string") {
        // Handle comma or pipe delimiters
        correctAnswers = rawAns.split(/[,|;]/).map((s: string) => s.trim()).filter(Boolean);
    }

    const correctSet = new Set(correctAnswers.map(a => normalizeText(a)));
    let score = 0;

    userSelection.forEach(sel => {
        if (correctSet.has(normalizeText(sel))) score += 1;
        else score -= 1;
    });

    // Minimum score is 0
    return Math.max(0, score);
}

function scoreFIB(question: any, userAnswers: string[] = []) {
    let score = 0;
    let details: string[] = [];
    let answerIdx = 0;

    for (let i = 1; i <= 15; i++) {
        const fieldName = `Q${i}_Answer`;
        if (question.hasOwnProperty(fieldName) || question[fieldName] !== undefined) {
            const correctAnswer = question[fieldName];
            if (correctAnswer) {
                const userAns = userAnswers[answerIdx] || "";
                const isCorrect = userAns.trim().toLowerCase() === ("" + correctAnswer).trim().toLowerCase();

                if (isCorrect) {
                    score++;
                    details.push(`Blank ${answerIdx + 1}: Correct (+1)`);
                } else {
                    details.push(`Blank ${answerIdx + 1}: Incorrect (0) - Expected "${correctAnswer}", Got "${userAns}"`);
                }
                answerIdx++;
            }
        }
    }

    if (details.length > 0) {
        console.log("FIB Partial Credit Details:", details);
    }
    return score;
}

function scoreMCSA(question: any, userChoice: string) {
    if (!userChoice) return 0;
    const correct = question.Q1_Answer || "";
    return userChoice.trim().toLowerCase() === ("" + correct).trim().toLowerCase() ? 1 : 0;
}

function scoreSMW(question: any, userChoice: string) {
    if (!userChoice) return 0;
    const correct = question.Q1_Answer || "";
    return userChoice.trim().toLowerCase() === ("" + correct).trim().toLowerCase() ? 1 : 0;
    return userChoice.trim().toLowerCase() === ("" + correct).trim().toLowerCase() ? 1 : 0;
}

function scoreHIW(question: any, userSelection: number[] = []) {
    // Dynamic scoring based on segment fields
    let correctIndices: number[] = [];
    let currentWordIndex = 0;

    for (let i = 1; i <= 15; i++) {
        // @ts-ignore
        const segmentText = question[`Q${i}`];
        if (segmentText) {
            const segmentWords = segmentText.split(/\s+/);

            // @ts-ignore
            const targetWord = question[`Q${i}_Opt1`]; // The incorrect word to identify

            if (targetWord) {
                // Find index of targetWord in this segment
                // Normalize both for comparison (remove basic punctuation?)
                // Assuming exact match or simple inclusion for now
                const localIndex = segmentWords.findIndex((w: string) =>
                    w.replace(/[.,;!?"]+/g, "") === targetWord.replace(/[.,;!?"]+/g, "") ||
                    w === targetWord
                );

                if (localIndex !== -1) {
                    correctIndices.push(currentWordIndex + localIndex);
                }
            }

            currentWordIndex += segmentWords.length;
        }
    }

    // If Q1_Answer is explicitly provided as indices, merge or prioritize? 
    // The user's example shows Q1_Answer as text ("bite"), so we rely on Opt1 ("item") for the target index.

    let score = 0;
    const correctSet = new Set(correctIndices);

    // +1 for correct, -1 for incorrect. Min 0.
    userSelection.forEach(idx => {
        if (correctSet.has(idx)) score += 1;
        else score -= 1;
    });

    return Math.max(0, score);
}

function scoreWFD(question: any, userResponse: string = "") {
    // WFD: +1 for each correctly spelled word
    const correctAnswer = question.Q1_Answer || "";
    if (!correctAnswer || !userResponse) return 0;

    // Split into words, normalize
    const correctWords = correctAnswer.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const userWords = userResponse.trim().toLowerCase().split(/\s+/).filter(Boolean);

    let score = 0;
    const correctWordsCopy = [...correctWords];

    // For each user word, check if it exists in correct words (remove on match to handle duplicates)
    userWords.forEach(userWord => {
        const idx = correctWordsCopy.indexOf(userWord);
        if (idx !== -1) {
            score++;
            correctWordsCopy.splice(idx, 1); // Remove matched word
        }
    });

    return score;
}
// Reusable Audio Progress Bar Component
const AudioProgressBar = ({ duration, currentTime }: { duration: number; currentTime: number }) => (
    <div style={{ width: '100%', maxWidth: '300px', height: '30px', backgroundColor: 'rgb(229, 231, 235)', borderRadius: '15px', position: 'relative', overflow: 'hidden', border: '2px solid rgb(156, 163, 175)', margin: '15px auto 0px auto' }}>
        <div
            style={{
                width: `${(duration && isFinite(duration)) ? (currentTime / duration) * 100 : 0}%`,
                height: '100%',
                backgroundColor: '#3b82f6',
                transition: 'width 0.1s linear'
            }}
        />
    </div>
);

// --- FIB Content Rendering ---

function ListeningFillInTheBlank({
    q,
    currentQuestionIndex,
    audioStatus,
    timeLeft,
    volume,
    setVolume,
    onAnswerChange,
    duration,
    currentTime,
    answers
}: {
    q: ListeningQuestion,
    currentQuestionIndex: number,
    audioStatus: string,
    timeLeft: number,
    volume: number,
    setVolume: (v: number) => void,
    onAnswerChange: (ans: string[]) => void,
    duration: number,
    currentTime: number,
    answers: string[]
}) {
    // Local state for immediate feedback
    const [userAnswers, setUserAnswers] = useState<string[]>([]);
    const [passageParts, setPassageParts] = useState<{ type: 'text' | 'input', content?: string, index?: number }[]>([]);
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    // Sync local state with props when question changes
    useEffect(() => {
        if (answers && answers.length > 0) {
            setUserAnswers(answers);
        } else {
            setUserAnswers([]);
        }
    }, [currentQuestionIndex]);

    useEffect(() => {
        const parts: { type: 'text' | 'input', content?: string, index?: number }[] = [];
        let blankIdx = 0;

        const processSegment = (text: string, segmentAnswerField?: string) => {
            if (!text) return;
            // Split by placeholders like ____ or [__]
            const segments = text.split(/(___+|\[__\])/g);
            let markersInSegment = 0;

            segments.forEach(seg => {
                if (/(___+|\[__\])/.test(seg)) {
                    parts.push({ type: 'input', index: blankIdx++ });
                    markersInSegment++;
                } else if (seg) {
                    parts.push({ type: 'text', content: seg });
                }
            });

            // If no internal markers were found, but there is an answer field for this segment,
            // we assume the blank follows the text.
            if (markersInSegment === 0 && segmentAnswerField !== undefined) {
                parts.push({ type: 'input', index: blankIdx++ });
            }
        };

        // Check if we have multi-field structure (Q1_Opt1, Q2, Q3...)
        const hasMultiFields = !!(q.Q1_Opt1 || q.Q2 || q.Q3);

        if (hasMultiFields) {
            if (q.Q1_Opt1) {
                const q1Ans = q.Q1_Answer !== undefined ? 'Q1_Answer' : (q.hasOwnProperty('Q1_Answer') ? 'Q1_Answer' : undefined);
                processSegment(q.Q1_Opt1, q1Ans);
            }
            for (let i = 2; i <= 15; i++) {
                const textField = q[`Q${i}`];
                if (textField) {
                    const ansField = q[`Q${i}_Answer`] !== undefined ? `Q${i}_Answer` : (q.hasOwnProperty(`Q${i}_Answer`) ? `Q${i}_Answer` : undefined);
                    processSegment(textField, ansField);
                }
            }
        } else if (q.Q1) {
            let textToProcess = q.Q1;
            if (textToProcess.includes("Instructions :")) {
                const lines = textToProcess.split(/\r?\n/).filter(l => l.trim() !== "");
                const passageLines = lines.filter(l =>
                    !l.includes("Instructions :") &&
                    !l.includes("PTE CORE") &&
                    !l.includes("LISTENING") &&
                    !/PART\s*-\s*\d+/i.test(l) &&
                    !/FILL IN THE BLANKS/i.test(l)
                );
                textToProcess = passageLines.join(" ");
            }
            processSegment(textToProcess);
        }

        setPassageParts(parts);

        // Initialize userAnswers if empty and we have a target length
        if ((!answers || answers.length === 0) && blankIdx > 0) {
            setUserAnswers(new Array(blankIdx).fill(""));
        }
    }, [q]); // Only depend on q to stabilize passageParts and structure

    const handleInputChange = (index: number, val: string) => {
        const newAns = [...userAnswers];
        newAns[index] = val;
        setUserAnswers(newAns);
        onAnswerChange(newAns);
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            const nextRef = inputRefs.current[index + 1];
            if (nextRef) nextRef.focus();
        }
    };

    let displayInstructions = q.Instructions || "You will hear a recording. Some words are missing. Type the missing words in each blank.";
    if (q.Q1 && q.Q1.includes("Instructions :")) {
        displayInstructions = q.Q1.split("Instructions :")[1].trim();
    }

    return (
        <section className="passage-wrapper">
            <div className="passage-outer">
                <div className="passage-inner">
                    <div className="mb-0">
                        <h2 className="passage-title mb-4">
                            Task 3 : Fill in the Blanks
                        </h2>
                    </div>

                    <div className="pte-qtext mb-10" style={{ fontWeight: '600' }}>
                        {displayInstructions}
                    </div>

                    <div className="mb-10 max-w-2xl mx-auto w-full">
                        <div className="bg-white border border-gray-300 rounded shadow-sm overflow-hidden p-6">
                            <div className="bg-[#f8f9fa] border border-gray-200 rounded-lg p-6 mb-4 flex flex-col items-center justify-center max-w-lg mx-auto">
                                <div className="mb-4 text-sm font-semibold text-gray-700">
                                    Status: <span className="text-[var(--accent)]">
                                        {audioStatus === 'preparation' ? `Beginning in ${timeLeft}s` : audioStatus === 'playing' ? 'Playing' : 'Completed'}
                                    </span>
                                </div>
                                <div className="w-full flex items-center gap-4 bg-white p-3 rounded border border-gray-300">
                                    <span className="text-xs font-bold text-gray-500 uppercase">Volume</span>
                                    <input
                                        type="range"
                                        min="0"
                                        max="100"
                                        value={volume}
                                        onChange={(e) => setVolume(Number(e.target.value))}
                                        className="w-full h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[var(--accent)]"
                                    />
                                </div>
                            </div>
                            <AudioProgressBar duration={duration} currentTime={currentTime} />
                        </div>
                    </div>

                    <div className="pte-fib-passage-box text-xl md:text-2xl leading-loose font-medium text-slate-800" style={{ textAlign: 'justify' }}>
                        {passageParts.map((part, i) => {
                            if (part.type === 'text') {
                                return <span key={i} className="whitespace-pre-wrap">{part.content}</span>;
                            }
                            if (part.type === 'input') {
                                const idx = part.index!;
                                return (
                                    <span key={i} className="inline-block relative mx-1 align-baseline">
                                        <input
                                            ref={el => { inputRefs.current[idx] = el }}
                                            type="text"
                                            value={userAnswers[idx] || ""}
                                            onChange={(e) => handleInputChange(idx, e.target.value)}
                                            onKeyDown={(e) => handleKeyDown(idx, e)}
                                            className="w-32 md:w-40 border-b-2 text-center px-1 py-1 outline-none transition-all duration-300 text-white font-bold border-slate-300 focus:border-[var(--accent)] hover:border-slate-400"
                                            style={{
                                                display: 'inline-block',
                                                fontSize: 'inherit',
                                                verticalAlign: 'baseline',
                                                backgroundColor: 'var(--accent)'
                                            }}
                                            placeholder="?"
                                            autoComplete="off"
                                        />
                                    </span>
                                );
                            }
                            return null;
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
}

// --- MCMA Component ---

function ListeningMCMA({
    q,
    audioStatus,
    timeLeft,
    volume,
    setVolume,
    onAnswerChange,
    duration,
    currentTime
}: {
    q: ListeningQuestion,
    audioStatus: string,
    timeLeft: number,
    volume: number,
    setVolume: (v: number) => void,
    onAnswerChange: (ans: string[]) => void,
    duration: number,
    currentTime: number
}) {
    const [options, setOptions] = useState<string[]>([]);
    const [selected, setSelected] = useState<string[]>([]);

    useEffect(() => {
        // Extract options from Q1_Opt1 ... Q1_OptN
        const opts: string[] = [];
        // Typically PTE options might go up to 7-8, checking 15 to be safe
        for (let i = 1; i <= 15; i++) {
            // @ts-ignore
            const val = q[`Q1_Opt${i}`];
            if (val) opts.push(val);
        }
        setOptions(opts);
        setSelected([]);
        onAnswerChange([]);
    }, [q]);

    const toggleOption = (opt: string) => {
        let newSel;
        if (selected.includes(opt)) {
            newSel = selected.filter(s => s !== opt);
        } else {
            newSel = [...selected, opt];
        }
        setSelected(newSel);
        onAnswerChange(newSel);
    };

    // Scoring Logic (Reference Implementation)
    // +1 for Correct, -1 for Incorrect, Min 0
    /*
    const calculateScore = (userSelected: string[], correctAnswers: string[]) => {
        let score = 0;
        userSelected.forEach(ans => {
            if (correctAnswers.includes(ans)) score += 1;
            else score -= 1;
        });
        return Math.max(0, score);
    };
    */

    return (
        <section className="passage-wrapper">
            <div className="passage-outer">
                <div className="passage-inner">

                    {/* 1. Header & Instructions Block */}
                    <div className="mb-8">
                        <h2 className="passage-title mb-4">
                            Task 2 : Multiple Choice Multiple Answer
                        </h2>



                        <div className="pte-qtext mb-4">
                            You will see a video or listening to an Audio. After the video or audio, answer the multiple choice question by selecting all the correct responses.
                            <br />
                            <br />
                            Note : You will need to select more than one response.
                            <br /><br />
                            {q.Q1}
                        </div>
                    </div>

                    {/* 2. Audio Player (Centered) */}
                    <div className="mb-10 max-w-2xl mx-auto w-full">
                        <div className="bg-white border border-gray-300 rounded shadow-sm overflow-hidden">


                            {/* Controls Container */}
                            <div className="p-6">
                                {/* New SST Style Controls */}
                                <div className="bg-[#f8f9fa] border border-gray-200 rounded-lg p-6 mb-6 flex flex-col items-center justify-center max-w-lg mx-auto">
                                    <div className="mb-4 text-sm font-semibold text-gray-700">
                                        Status: <span className="text-[var(--accent)]">
                                            {audioStatus === 'preparation' ? `Beginning in ${timeLeft}s` : audioStatus === 'playing' ? 'Playing' : 'Completed'}
                                        </span>
                                    </div>
                                    <div className="w-full flex items-center gap-4 bg-white p-3 rounded border border-gray-300">
                                        <span className="text-xs font-bold text-gray-500 uppercase">Volume</span>
                                        <input
                                            type="range"
                                            min="0"
                                            max="100"
                                            value={volume}
                                            onChange={(e) => setVolume(Number(e.target.value))}
                                            className="w-full h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[var(--accent)]"
                                        />
                                    </div>
                                </div>

                                <AudioProgressBar duration={duration} currentTime={currentTime} />
                            </div>
                        </div>
                    </div>

                    {/* 3. Question Options (List) */}
                    <div className="max-w-3xl mx-auto w-full">
                        <div className="pte-options pte-options--vertical">
                            {options.map((opt, i) => (
                                <label key={i} className="pte-option pte-option--mcma">
                                    <input
                                        type="checkbox"
                                        checked={selected.includes(opt)}
                                        onChange={() => toggleOption(opt)}
                                    />
                                    <span className="pte-option-label">{opt}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                </div>
            </div>
        </section>
    );
}

function ListeningMCSA({
    q,
    audioStatus,
    timeLeft,
    volume,
    setVolume,
    duration,
    currentTime,
    onAnswerChange,
    currentQuestionIndex,
    value = ""
}: {
    q: ListeningQuestion,
    audioStatus: string,
    timeLeft: number,
    volume: number,
    setVolume: (v: number) => void,
    duration: number,
    currentTime: number,
    onAnswerChange: (ans: string) => void,
    currentQuestionIndex: number,
    value?: string
}) {
    const [options, setOptions] = useState<string[]>([]);
    const [selected, setSelected] = useState<string>(value);

    // Initial load of options
    useEffect(() => {
        const opts: string[] = [];
        for (let i = 1; i <= 6; i++) {
            const val = q[`Q1_Opt${i}`];
            if (val) opts.push(val);
        }
        setOptions(opts);
        setSelected(value);
    }, [q, value]);

    const handleSelect = (opt: string) => {
        setSelected(opt);
        onAnswerChange(opt);
    };

    return (
        <section className="passage-wrapper">
            <div className="passage-outer">
                <div className="passage-inner">

                    {/* 1. Header & Instructions Block */}
                    <div className="mb-8">
                        <h2 className="passage-title mb-4">
                            Task 4 : Multiple Choice Single Answer
                        </h2>

                        <div className="pte-qtext mb-4" style={{ fontWeight: '600' }}>
                            You will hear a recording. Answer the multiple-choice question by selecting the correct response. Only one response is correct.
                            <br /><br />
                            {q.Q1}
                        </div>
                    </div>

                    {/* 2. Audio Player (Centered) */}
                    <div className="mb-10 max-w-2xl mx-auto w-full">
                        <div className="bg-white border border-gray-300 rounded shadow-sm overflow-hidden p-6">
                            <div className="bg-[#f8f9fa] border border-gray-200 rounded-lg p-6 mb-6 flex flex-col items-center justify-center max-w-lg mx-auto">
                                <div className="mb-4 text-sm font-semibold text-gray-700">
                                    Status: <span className="text-[var(--accent)]">
                                        {audioStatus === 'preparation' ? `Beginning in ${timeLeft}s` : audioStatus === 'playing' ? 'Playing' : 'Completed'}
                                    </span>
                                </div>
                                <div className="w-full flex items-center gap-4 bg-white p-3 rounded border border-gray-300">
                                    <span className="text-xs font-bold text-gray-500 uppercase">Volume</span>
                                    <input
                                        type="range"
                                        min="0"
                                        max="100"
                                        value={volume}
                                        onChange={(e) => setVolume(Number(e.target.value))}
                                        className="w-full h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[var(--accent)]"
                                    />
                                </div>
                            </div>
                            <AudioProgressBar duration={duration} currentTime={currentTime} />
                        </div>
                    </div>

                    {/* 3. Question Options (List) */}
                    <div className="max-w-3xl mx-auto w-full">
                        <div className="pte-options pte-options--vertical">
                            {options.map((opt, i) => (
                                <label key={i} className="pte-option pte-option--mcsa">
                                    <input
                                        type="radio"
                                        name={`mcsa-option-${currentQuestionIndex}`}
                                        checked={selected === opt}
                                        onChange={() => handleSelect(opt)}
                                    />
                                    <span className="pte-option-label">{opt}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                </div>
            </div>
        </section>
    );
}

function ListeningSMW({
    q,
    audioStatus,
    timeLeft,
    volume,
    setVolume,
    duration,
    currentTime,
    onAnswerChange,
    currentQuestionIndex,
    value = ""
}: {
    q: ListeningQuestion,
    audioStatus: string,
    timeLeft: number,
    volume: number,
    setVolume: (v: number) => void,
    duration: number,
    currentTime: number,
    onAnswerChange: (ans: string) => void,
    currentQuestionIndex: number,
    value?: string
}) {
    const [options, setOptions] = useState<string[]>([]);
    const [selected, setSelected] = useState<string>(value);

    // Initial load of options
    useEffect(() => {
        const opts: string[] = [];
        for (let i = 1; i <= 6; i++) {
            const val = q[`Q1_Opt${i}`];
            if (val) opts.push(val);
        }
        setOptions(opts);
        setSelected(value);
    }, [q, value]);

    const handleSelect = (opt: string) => {
        setSelected(opt);
        onAnswerChange(opt);
    };

    return (
        <section className="passage-wrapper">
            <div className="passage-outer">
                <div className="passage-inner">

                    {/* 1. Header & Instructions Block */}
                    <div className="mb-8">
                        <h2 className="passage-title mb-4">
                            Task 5: Select Missing Word
                        </h2>

                        <div className="pte-qtext mb-4" style={{ fontWeight: '600' }}>
                            You will hear a recording. At the end of the recording, one or more words have been replaced by a beep. Select the correct option to complete the recording.
                        </div>
                    </div>

                    {/* 2. Audio Player (Centered) */}
                    <div className="mb-10 max-w-2xl mx-auto w-full">
                        <div className="bg-white border border-gray-300 rounded shadow-sm overflow-hidden p-6">
                            <div className="bg-[#f8f9fa] border border-gray-200 rounded-lg p-6 mb-6 flex flex-col items-center justify-center max-w-lg mx-auto">
                                <div className="mb-4 text-sm font-semibold text-gray-700">
                                    Status: <span className="text-[var(--accent)]">
                                        {audioStatus === 'preparation' ? `Beginning in ${timeLeft}s` : audioStatus === 'playing' ? 'Playing' : 'Completed'}
                                    </span>
                                </div>
                                <div className="w-full flex items-center gap-4 bg-white p-3 rounded border border-gray-300">
                                    <span className="text-xs font-bold text-gray-500 uppercase">Volume</span>
                                    <input
                                        type="range"
                                        min="0"
                                        max="100"
                                        value={volume}
                                        onChange={(e) => setVolume(Number(e.target.value))}
                                        className="w-full h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[var(--accent)]"
                                    />
                                </div>
                            </div>
                            <AudioProgressBar duration={duration} currentTime={currentTime} />
                        </div>
                    </div>

                    {/* 3. Question Options (List) */}
                    <div className="max-w-3xl mx-auto w-full">
                        <div className="pte-options pte-options--vertical">
                            {options.map((opt, i) => (
                                <label key={i} className="pte-option pte-option--mcsa">
                                    <input
                                        type="radio"
                                        name={`smw-option-${currentQuestionIndex}`}
                                        checked={selected === opt}
                                        onChange={() => handleSelect(opt)}
                                    />
                                    <span className="pte-option-label">{opt}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                </div>
            </div>
        </section>
    );
}

function ListeningHIW({
    q,
    audioStatus,
    timeLeft,
    volume,
    setVolume,
    duration,
    currentTime,
    onAnswerChange,
    currentQuestionIndex,
    value = "[]"
}: {
    q: ListeningQuestion,
    audioStatus: string,
    timeLeft: number,
    volume: number,
    setVolume: (v: number) => void,
    duration: number,
    currentTime: number,
    onAnswerChange: (ans: string) => void,
    currentQuestionIndex: number,
    value?: string
}) {
    const [words, setWords] = useState<{ word: string, index: number }[]>([]);
    const [selectedIndices, setSelectedIndices] = useState<number[]>([]);

    useEffect(() => {
        // Aggregate text from Q1...Q15
        let text = "";
        for (let i = 1; i <= 15; i++) {
            // @ts-ignore
            const part = q[`Q${i}`];
            if (part) {
                // Determine if we need a space separator
                text += (text.length > 0 ? " " : "") + part;
            }
        }
        if (!text) text = q.Q1 || "";

        const w = text.split(/\s+/).map((word, i) => ({ word, index: i }));
        setWords(w);
    }, [q]);

    useEffect(() => {
        // Only sync from prop when question changes, not on every click
        try {
            const parsed = JSON.parse(value || "[]");
            if (Array.isArray(parsed)) setSelectedIndices(parsed);
        } catch {
            setSelectedIndices([]);
        }
    }, [currentQuestionIndex]);

    const toggleWord = (index: number) => {
        let newSel;
        if (selectedIndices.includes(index)) {
            newSel = selectedIndices.filter(i => i !== index);
        } else {
            newSel = [...selectedIndices, index];
        }
        setSelectedIndices(newSel);
        onAnswerChange(JSON.stringify(newSel));
    };

    return (
        <section className="passage-wrapper">
            <div className="passage-outer">
                <div className="passage-inner">

                    {/* 1. Header & Instructions Block */}
                    <div className="mb-8">
                        <h2 className="passage-title mb-4">
                            Task 6 : Highlight Incorrect Words
                        </h2>

                        <div className="pte-qtext mb-4" style={{ fontWeight: '600' }}>
                            You will hear a recording. Below is a transcript of the recording. Some words in the transcript differ from what the speaker(s) said. Please click on the words that are different.
                        </div>
                    </div>

                    {/* 2. Audio Player (Centered) */}
                    <div className="mb-10 max-w-2xl mx-auto w-full">
                        <div className="bg-white border border-gray-300 rounded shadow-sm overflow-hidden p-6">
                            <div className="bg-[#f8f9fa] border border-gray-200 rounded-lg p-6 mb-6 flex flex-col items-center justify-center max-w-lg mx-auto">
                                <div className="mb-4 text-sm font-semibold text-gray-700">
                                    Status: <span className="text-[var(--accent)]">
                                        {audioStatus === 'preparation' ? `Beginning in ${timeLeft}s` : audioStatus === 'playing' ? 'Playing' : 'Completed'}
                                    </span>
                                </div>
                                <div className="w-full flex items-center gap-4 bg-white p-3 rounded border border-gray-300">
                                    <span className="text-xs font-bold text-gray-500 uppercase">Volume</span>
                                    <input
                                        type="range"
                                        min="0"
                                        max="100"
                                        value={volume}
                                        onChange={(e) => setVolume(Number(e.target.value))}
                                        className="w-full h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[var(--accent)]"
                                    />
                                </div>
                            </div>
                            <AudioProgressBar duration={duration} currentTime={currentTime} />
                        </div>
                    </div>

                    {/* 3. Transcript with Clickable Words */}

                    <div className="pte-fib-passage-box text-xl md:text-2xl leading-loose font-medium text-slate-800" style={{ textAlign: 'justify', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                        {words.map((w, i) => (
                            <span
                                key={i}
                                onClick={() => toggleWord(w.index)}
                                className={`cursor-pointer inline-block mx-0.5 px-1 rounded select-none transition-colors duration-200 hover:bg-yellow-100 ${selectedIndices.includes(w.index) ? 'bg-yellow-400 text-slate-900 border-[8px] border-blue-500 shadow-sm' : ''}`}
                            >
                                {w.word}
                            </span>
                        ))}
                    </div>

                </div>
            </div>
        </section>
    );
}

function ListeningWFD({
    q,
    audioStatus,
    timeLeft,
    volume,
    setVolume,
    duration,
    currentTime,
    onAnswerChange,
    currentQuestionIndex,
    value = "",
    wordCount
}: {
    q: ListeningQuestion,
    audioStatus: string,
    timeLeft: number,
    volume: number,
    setVolume: (v: number) => void,
    duration: number,
    currentTime: number,
    onAnswerChange: (ans: string) => void,
    currentQuestionIndex: number,
    value?: string,
    wordCount: number
}) {
    const [localText, setLocalText] = useState<string>(value);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        setLocalText(value);
    }, [currentQuestionIndex]);

    const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newText = e.target.value;
        setLocalText(newText);
        onAnswerChange(newText);
    };

    const countWords = (text: string) => {
        return text.trim().split(/\s+/).filter(Boolean).length;
    };

    return (
        <section className="passage-wrapper">
            <div className="passage-outer">
                <div className="passage-inner">

                    {/* 1. Header & Instructions Block */}
                    <div className="mb-8">
                        <h2 className="passage-title mb-4">
                            Task 7 : Write from Dictation
                        </h2>

                        <div className="pte-qtext mb-4" style={{ fontWeight: '600' }}>
                            You will hear a sentence. Type the sentence in the response box exactly as you hear it. Write as much of the sentence as you can. You will hear the sentence only once.
                        </div>
                    </div>

                    {/* 2. Audio Player (Centered) */}
                    <div className="mb-10 max-w-2xl mx-auto w-full">
                        <div className="bg-white border border-gray-300 rounded shadow-sm overflow-hidden p-6">
                            <div className="bg-[#f8f9fa] border border-gray-200 rounded-lg p-6 mb-6 flex flex-col items-center justify-center max-w-lg mx-auto">
                                <div className="mb-4 text-sm font-semibold text-gray-700">
                                    Status: <span className="text-[var(--accent)]">
                                        {audioStatus === 'preparation' ? `Beginning in ${timeLeft}s` : audioStatus === 'playing' ? 'Playing' : 'Completed'}
                                    </span>
                                </div>
                                <div className="w-full flex items-center gap-4 bg-white p-3 rounded border border-gray-300">
                                    <span className="text-xs font-bold text-gray-500 uppercase">Volume</span>
                                    <input
                                        type="range"
                                        min="0"
                                        max="100"
                                        value={volume}
                                        onChange={(e) => setVolume(Number(e.target.value))}
                                        className="w-full h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[var(--accent)]"
                                    />
                                </div>
                            </div>
                            <AudioProgressBar duration={duration} currentTime={currentTime} />
                        </div>
                    </div>

                    {/* 3. Response Text Area */}
                    <div className="max-w-3xl mx-auto w-full">
                        <div className="bg-white border border-gray-300 rounded shadow-sm p-6">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Type your response:
                            </label>
                            <textarea
                                ref={textareaRef}
                                className="block box-border h-32 p-3 border border-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none text-gray-800 text-base resize-none shadow-inner"
                                style={{ width: "100%" }}
                                value={localText}
                                onChange={handleTextChange}
                                placeholder="Type the sentence you hear..."
                                spellCheck={false}
                            ></textarea>
                            <div className="flex justify-end mt-2">
                                <div className="font-semibold text-sm text-gray-600">
                                    Word Count: {countWords(localText)}
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </section>
    );
}

interface ListeningQuestion {
    _id: string;
    Questiontype: string;
    Task: number;
    Taskaudio1: string;
    Q1?: string; // Prompt text
    Test_number: number;
    [key: string]: any;
}

interface PteCoreListeningTestProps {
    isFullTest?: boolean;
    onSectionComplete?: () => void;
}

const PteCoreListeningTest: React.FC<PteCoreListeningTestProps> = ({ isFullTest, onSectionComplete }) => {
    const { testId = '1' } = useParams<{ testId: string }>();
    const navigate = useNavigate();
    const [questions, setQuestions] = useState<ListeningQuestion[]>([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [textResponse, setTextResponse] = useState("");
    const [wordCount, setWordCount] = useState(0);

    // Audio State
    const [audioStatus, setAudioStatus] = useState<"preparation" | "playing" | "finished">("preparation");
    const [timeLeft, setTimeLeft] = useState(12); // 12 seconds prep
    const [volume, setVolume] = useState(50);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);

    const audioRef = useRef<HTMLAudioElement | null>(null);

        const [results, setResults] = useState<ListeningResult[]>([]);
    const [showScore, setShowScore] = useState(false);
    const [taskTimeLeft, setTaskTimeLeft] = useState(480);

    const reduxUser = useSelector((state: any) => state.authReducer?.authData);
    const user = getStoredUser(reduxUser);

    const recordScore = async (index: number) => {
        const q = questions[index];
        let score = 0;
        let maxScore = 1;
        let correctAnswer = "";
        let uResponse = textResponse;
        let traits: any = undefined;

        try {
            if (q.Questiontype === "Summarize Spoken Text" || q.Questiontype === "SST" || q.Task === 1) {
                // OpenAI Scoring for SST
                try {
                    const response = await submitPteCoreListeningSST(q._id, textResponse, user?.name || "Guest User", user?._id || user?.id);
                    if (response.data && response.data.autoScore) {
                        const auto = response.data.autoScore;
                        score = auto.overall?.score || 0;
                        maxScore = auto.overall?.maxScore || 10;
                        traits = auto; // Store the whole autoScore object which contains traits
                        correctAnswer = "AI Evaluation provides detailed feedback below.";
                    }
                } catch (err) {
                    console.error("SST Scoring Error:", err);
                    const words = textResponse.trim().split(/\s+/).filter(Boolean).length;
                    score = getFormScore(words);
                    maxScore = 10;
                    correctAnswer = "AI Scoring Unavailable. Score based on word count (Form) only.";
                }

            } else if (q.Questiontype === "MCMA" || q.Questiontype === "Multiple Choice, Multiple Answers" || q.Task === 2) {
                const selected = JSON.parse(textResponse || "[]");
                score = scoreMCMA(q, selected);
                const rawAns = q.Q1_Answer || q.Answer;
                let corrects: string[] = [];
                if (Array.isArray(rawAns)) corrects = rawAns;
                else if (typeof rawAns === "string") corrects = rawAns.split(/[,|;]/).map(s => s.trim()).filter(Boolean);
                correctAnswer = corrects.join(", ");
                maxScore = corrects.length;
                uResponse = selected.join(", ");

            } else if (q.Questiontype === "MCSA" || q.Questiontype === "Multiple Choice, Single Answer" || q.Task === 4) {
                score = scoreMCSA(q, textResponse);
                correctAnswer = q.Q1_Answer || q.Answer || "";
                maxScore = 1;

            } else if (q.Questiontype === "Select Missing Word" || q.Questiontype === "SMW" || q.Task === 5) {
                score = scoreSMW(q, textResponse);
                correctAnswer = q.Q1_Answer || q.Answer || "";
                maxScore = 1;

            } else if (q.Questiontype === "FIB" || q.Questiontype === "Fill in the Blank" || q.Task === 3) {
                const answers = JSON.parse(textResponse || "[]");
                score = scoreFIB(q, answers);
                const cAnsArr: string[] = [];
                for (let i = 1; i <= 15; i++) {
                    if (q[`Q${i}_Answer`]) cAnsArr.push(q[`Q${i}_Answer`]);
                }
                correctAnswer = cAnsArr.join(", ");
                maxScore = cAnsArr.length;
                uResponse = answers.join(", ");

            } else if (q.Questiontype === "Highlight Incorrect Words" || q.Questiontype === "HIW" || q.Task === 6) {
                const selected = JSON.parse(textResponse || "[]");
                score = scoreHIW(q, selected);
                let correctIndices: number[] = [];
                let currentWordIndex = 0;
                let wordsArr: string[] = [];
                for (let i = 1; i <= 15; i++) {
                    const segmentText = q[`Q${i}`];
                    if (segmentText) {
                        const segmentWords = segmentText.split(/\s+/);
                        const targetWord = q[`Q${i}_Opt1`];
                        if (targetWord) {
                            const localIndex = segmentWords.findIndex((w: string) =>
                                w.replace(/[.,;!?\"]+/g, "") === targetWord.replace(/[.,;!?\"]+/g, "") ||
                                w === targetWord
                            );
                            if (localIndex !== -1) correctIndices.push(currentWordIndex + localIndex);
                        }
                        segmentWords.forEach((word: string) => wordsArr.push(word));
                        currentWordIndex += segmentWords.length;
                    }
                }
                correctAnswer = correctIndices.map(idx => wordsArr[idx]).join(", ");
                maxScore = correctIndices.length;
                uResponse = selected.map((idx: number) => wordsArr[idx]).join(", ");

            } else if (q.Questiontype === "Write from Dictation" || q.Questiontype === "WFD" || q.Task === 7) {
                const rawScore = scoreWFD(q, textResponse);
                correctAnswer = q.Q1_Answer || "";
                const totalWords = correctAnswer.trim().split(/\s+/).filter(Boolean).length;
                maxScore = 3;
                score = totalWords > 0 ? Number(((rawScore / totalWords) * 3).toFixed(1)) : 0;
            }

            const newResult: ListeningResult = {
                taskId: q._id,
                taskNumber: index + 1,
                taskType: q.Questiontype || `Task ${q.Task}`,
                userResponse: uResponse,
                correctAnswer: correctAnswer,
                score: score,
                maxScore: maxScore,
                traits: traits
            };

            setResults(prev => {
                const filtered = prev.filter(r => r.taskId !== q._id);
                return [...filtered, newResult].sort((a, b) => a.taskNumber - b.taskNumber);
            });

            console.log(`Recorded result for Question ${index + 1}: Score = ${score}`);
        } catch (e) {
            console.error("Error scoring question", e);
        }
    };

    const handlePrev = async () => {
        if (currentQuestionIndex > 0) {
            await recordScore(currentQuestionIndex);
            const nextIdx = currentQuestionIndex - 1;
            const nextQ = questions[nextIdx];
            const isMCMA = nextQ.Questiontype === "Multiple Choice, Multiple Answers" || nextQ.Questiontype === "MCMA" || nextQ.Task === 2;
            const isMCSA = nextQ.Questiontype === "Multiple Choice, Single Answer" || nextQ.Questiontype === "MCSA" || nextQ.Task === 4;
            const isFIB = nextQ.Questiontype === "Fill in the Blank" || nextQ.Questiontype === "FIB" || nextQ.Task === 3;
            const isSMW = nextQ.Questiontype === "Select Missing Word" || nextQ.Questiontype === "SMW" || nextQ.Task === 5;
            const isHIW = nextQ.Questiontype === "Highlight Incorrect Words" || nextQ.Questiontype === "HIW" || nextQ.Task === 6;
            const isWFD = nextQ.Questiontype === "Write from Dictation" || nextQ.Questiontype === "WFD" || nextQ.Task === 7;

            setCurrentQuestionIndex(prev => prev - 1);
            setAudioStatus('preparation');
            setTimeLeft((isFIB || isSMW || isWFD) ? 7 : (isMCMA || isMCSA || isHIW) ? 10 : 12);
            setTextResponse("");
            setWordCount(0);
            setTaskTimeLeft(480);
            setDuration(0);
            setCurrentTime(0);
        }
    };

    const handleNext = async () => {
        // Stop audio if playing
        if (audioRef.current) {
            audioRef.current.pause();
        }

        // Record score for current question
        setLoading(true); // Show loading while grading if it's SST
        await recordScore(currentQuestionIndex);
        setLoading(false);

        if (currentQuestionIndex < questions.length - 1) {
            const nextIdx = currentQuestionIndex + 1;
            const nextQ = questions[nextIdx];
            const isMCMA = nextQ.Questiontype === "Multiple Choice, Multiple Answers" || nextQ.Questiontype === "MCMA" || nextQ.Task === 2;
            const isMCSA = nextQ.Questiontype === "Multiple Choice, Single Answer" || nextQ.Questiontype === "MCSA" || nextQ.Task === 4;
            const isFIB = nextQ.Questiontype === "Fill in the Blank" || nextQ.Questiontype === "FIB" || nextQ.Task === 3;
            const isSMW = nextQ.Questiontype === "Select Missing Word" || nextQ.Questiontype === "SMW" || nextQ.Task === 5;
            const isHIW = nextQ.Questiontype === "Highlight Incorrect Words" || nextQ.Questiontype === "HIW" || nextQ.Task === 6;
            const isWFD = nextQ.Questiontype === "Write from Dictation" || nextQ.Questiontype === "WFD" || nextQ.Task === 7;

            setCurrentQuestionIndex(prev => prev + 1);
            // Reset states for next question
            setAudioStatus('preparation');
            setTimeLeft((isFIB || isSMW || isWFD) ? 7 : (isMCMA || isMCSA || isHIW) ? 10 : 12);
            setTextResponse("");
            setWordCount(0);
            setTaskTimeLeft(480); // Reset timer 8 mins
            setDuration(0);
            setCurrentTime(0);
        } else {
            // Test complete
            setShowScore(true);
        }
    };

    useEffect(() => {
        const loadTest = async () => {
            try {
                // Fetch all questions for the test
                const response = await fetchPteCoreListeningTest(Number(testId));
                // Filter specifically for SST if needed, or sort by Task/Order
                // The user request says "Start with Task 1 or Questiontype SST"
                // We'll filter/sort to ensure SST is first or just load all.
                // Assuming backend returns correctly ordered list or we sort it.
                // For now, let's assume we want to work on ALL listening tasks, but effectively render SST first.
                // If the dataset contains mixed types, good.

                // Load all questions without filtering for specific tasks
                // This ensures MCMA (Task 2) and others are available
                const finalQuestions = response.data;

                setQuestions(finalQuestions);

                if (finalQuestions.length > 0) {
                    const firstQ = finalQuestions[0];
                    const isMCMA = firstQ.Questiontype === "Multiple Choice, Multiple Answers" || firstQ.Questiontype === "MCMA" || firstQ.Task === 2;
                    const isMCSA = firstQ.Questiontype === "Multiple Choice, Single Answer" || firstQ.Questiontype === "MCSA" || firstQ.Task === 4;
                    const isFIB = firstQ.Questiontype === "Fill in the Blank" || firstQ.Questiontype === "FIB" || firstQ.Task === 3;
                    const isSMW = firstQ.Questiontype === "Select Missing Word" || firstQ.Questiontype === "SMW" || firstQ.Task === 5;
                    const isHIW = firstQ.Questiontype === "Highlight Incorrect Words" || firstQ.Questiontype === "HIW" || firstQ.Task === 6;
                    const isWFD = firstQ.Questiontype === "Write from Dictation" || firstQ.Questiontype === "WFD" || firstQ.Task === 7;
                    setTimeLeft((isFIB || isSMW || isWFD) ? 7 : (isMCMA || isMCSA || isHIW) ? 10 : 12);
                }
            } catch (error) {
                console.error("Failed to fetch listening test", error);
            } finally {
                setLoading(false);
            }
        };
        loadTest();
    }, [testId]);

    // Timer Logic for Preparation
    useEffect(() => {
        if (audioStatus === 'preparation' && timeLeft > 0) {
            const timer = setInterval(() => {
                setTimeLeft((prev) => prev - 1);
            }, 1000);
            return () => clearInterval(timer);
        } else if (audioStatus === 'preparation' && timeLeft === 0) {
            setAudioStatus('playing');
            if (audioRef.current) {
                audioRef.current.play();
            }
        }
    }, [audioStatus, timeLeft]);

    // Timer Logic for Task (8 minutes)

    useEffect(() => {
        if (audioStatus !== 'preparation' && taskTimeLeft > 0) {
            const timer = setInterval(() => {
                setTaskTimeLeft((prev) => prev - 1);
            }, 1000);
            return () => clearInterval(timer);
        } else if (taskTimeLeft === 0) {
            // Time is up
            handleNext();
        }
    }, [audioStatus, taskTimeLeft]);

    const formatTime = (seconds: number) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m}:${s < 10 ? '0' : ''}${s}`;
    };

    const handleWordCount = (text: string) => {
        setTextResponse(text);
        const words = text.trim().split(/\s+/).filter(w => w.length > 0).length;
        setWordCount(text.trim() === "" ? 0 : words);
    };

    const getFormScore = (count: number) => {
        if (count >= 20 && count <= 70) return 2;
        if ((count >= 10 && count <= 49) || (count >= 71 && count <= 40)) return 1;
        return 0;
    };

    const handleCut = () => {
        navigator.clipboard.writeText(textResponse);
        setTextResponse("");
        setWordCount(0);
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(textResponse);
    };

    const handlePaste = async () => {
        try {
            const text = await navigator.clipboard.readText();
            setTextResponse((prev) => prev + text);
            handleWordCount(textResponse + text);
        } catch (err) {
            console.error("Failed to paste", err);
        }
    };

    // Volume control
    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.volume = volume / 100;
        }
    }, [volume]);

    const handleRetry = () => {
        setCurrentQuestionIndex(0);
        setResults([]);
        setShowScore(false);
        setAudioStatus('preparation');
        setTimeLeft(12);
        setTextResponse("");
        setWordCount(0);
    };

    if (showScore) {
        return (
            <PteCoreListeningScore
                results={results}
                onRetry={handleRetry}
                onNext={() => {
                    if (isFullTest && onSectionComplete) onSectionComplete();
                    else navigate('/pte/dashboardpte');
                }}
                nextLabel={isFullTest ? "Next Section" : "Finish Test"}
            />
        );
    }

    if (loading) return (
        <div className="ptest-root">
            <div className="spinner-overlay"><div className="spinner" /></div>
        </div>
    );

    if (questions.length === 0) return (
        <div className="ptest-root">
            <div className="ptest-header">
                <div></div>
            </div>
            <div className="max-w-4xl mx-auto mt-20 p-8 text-center">
                <h2 className="text-xl font-bold mb-4">No listening questions found.</h2>
                <button onClick={() => navigate('/pte/dashboardpte')} className="btn btn-primary">Return to Dashboard</button>
            </div>
        </div>
    );

    const currentQuestion = questions[currentQuestionIndex];
    // Normalize audio URL
    const audioUrl = getAssetUrl(currentQuestion.Taskaudio1 || "");

    return (
        <div className="ptest-root">
            {/* Header */}
            <div className="ptest-header">
                <div className="ptest-title">
                    <h1>PTE Core Listening Test {testId}</h1>
                    <div className="progress-indicator">Item {currentQuestionIndex + 1} of {questions.length}</div>
                </div>
                <div className="right-controls">
                    <div className="timer">
                        <span className="timer-icon">⏱</span>
                        <span>
                            {audioStatus === 'preparation' ? `Prep: ${timeLeft}s` : formatTime(taskTimeLeft)}
                        </span>
                    </div>
                </div>
            </div>

            <div className="ptest-main ptest-main--single-col">
                <div className="pte-card">
                    {/* Hidden Audio Element */}
                    <audio
                        key={currentQuestion._id}
                        ref={audioRef}
                        src={audioUrl}
                        onEnded={() => setAudioStatus('finished')}
                        onTimeUpdate={(e) => {
                            const cur = e.currentTarget.currentTime;
                            const dur = e.currentTarget.duration;
                            setCurrentTime(cur);
                            if (dur && isFinite(dur) && dur !== duration) {
                                setDuration(dur);
                            }
                        }}
                        onLoadedMetadata={(e) => {
                            const dur = e.currentTarget.duration;
                            if (isFinite(dur)) setDuration(dur);
                        }}
                        onDurationChange={(e) => {
                            const dur = e.currentTarget.duration;
                            if (isFinite(dur)) setDuration(dur);
                        }}
                        preload="auto"
                        style={{ display: 'none' }}
                    />


                    {/* Common Audio Player UI - Hide for types with custom layouts */}
                    {!(currentQuestion.Questiontype === "Summarize Spoken Text" ||
                        currentQuestion.Task === 1 ||
                        currentQuestion.Questiontype === "Multiple Choice, Multiple Answers" ||
                        currentQuestion.Questiontype === "MCMA" ||
                        currentQuestion.Task === 2 ||
                        currentQuestion.Questiontype === "Multiple Choice, Single Answer" ||
                        currentQuestion.Questiontype === "MCSA" ||
                        currentQuestion.Task === 4 ||
                        currentQuestion.Questiontype === "Select Missing Word" ||
                        currentQuestion.Questiontype === "SMW" ||
                        currentQuestion.Task === 5 ||
                        currentQuestion.Questiontype === "Fill in the Blank" ||
                        currentQuestion.Questiontype === "FIB" ||
                        currentQuestion.Questiontype === "Highlight Incorrect Words" ||
                        currentQuestion.Questiontype === "HIW" ||
                        currentQuestion.Task === 6 ||
                        currentQuestion.Questiontype === "Write from Dictation" ||
                        currentQuestion.Questiontype === "WFD" ||
                        currentQuestion.Task === 7) && (
                            <div className="bg-[#f8f9fa] border border-gray-200 rounded-lg p-6 mb-6 flex flex-col items-center justify-center max-w-lg mx-auto">
                                <div className="mb-4 text-sm font-semibold text-gray-700">
                                    Status: <span className="text-[#0b84b1]">
                                        {audioStatus === 'preparation' ? `Beginning in ${timeLeft} seconds.` :
                                            audioStatus === 'playing' ? "Playing" : "Finished"}
                                    </span>
                                </div>

                                <div className="w-full flex items-center gap-4 bg-white p-3 rounded border border-gray-300">
                                    <span className="text-xs font-bold text-gray-500 uppercase">Volume</span>
                                    <input
                                        type="range"
                                        min="0"
                                        max="100"
                                        value={volume}
                                        onChange={(e) => setVolume(Number(e.target.value))}
                                        className="w-full h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[var(--accent)]"
                                    />
                                </div>
                            </div>
                        )}

                    {/* Conditional Rendering based on Question Type */}
                    {(currentQuestion.Questiontype === "Summarize Spoken Text" || currentQuestion.Questiontype === "SST" || currentQuestion.Task === 1) ? (
                        <section className="passage-wrapper">
                            <div className="passage-outer">
                                <div className="passage-inner">
                                    <h2 className="passage-title mb-4">
                                        Task {currentQuestion.Task || 1}: {(currentQuestion.Questiontype === "SST" ? "Summarize Spoken Text" : currentQuestion.Questiontype) || "Summarize Spoken Text"}
                                    </h2>
                                    {/* SST Instructions */}
                                    <div className="pte-qtext mb-4">
                                        You will hear a recording of a talk about a topic.
                                        <br />
                                        <br />
                                        Write between 20 to 30 words explaining the main points of the talk for someone who was not present at the talk.
                                        <br />
                                        <br />
                                        You have 8 minutes to finish this task.
                                    </div>

                                    {/* Integrated Audio Player for SST */}
                                    <div className="bg-[#f8f9fa] border border-gray-200 rounded-lg p-6 mb-6 flex flex-col items-center justify-center max-w-lg mx-auto">
                                        <div className="mb-4 text-sm font-semibold text-gray-700">
                                            Status: <span className="text-[#0b84b1]">
                                                {audioStatus === 'preparation' ? `Beginning in ${timeLeft} seconds.` :
                                                    audioStatus === 'playing' ? "Playing" : "Finished"}
                                            </span>
                                        </div>

                                        <div className="w-full flex items-center gap-4 bg-white p-3 rounded border border-gray-300">
                                            <span className="text-xs font-bold text-gray-500 uppercase">Volume</span>
                                            <input
                                                type="range"
                                                min="0"
                                                max="100"
                                                value={volume}
                                                onChange={(e) => setVolume(Number(e.target.value))}
                                                className="w-full h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[var(--accent)]"
                                            />
                                        </div>

                                        <AudioProgressBar duration={duration} currentTime={currentTime} />
                                    </div>

                                    {/* SST Editor */}
                                    <div className="mb-2">
                                        <textarea
                                            className="block box-border h-32 p-3 border border-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none text-gray-800 text-base resize-none shadow-inner mt-4"
                                            style={{ width: "100%" }}
                                            value={textResponse}
                                            onChange={(e) => handleWordCount(e.target.value)}
                                            placeholder="Type your response here..."
                                            spellCheck={false}
                                        ></textarea>
                                    </div>

                                    <div className="flex justify-between items-center mt-2">
                                        <div className="flex gap-2">
                                            <button onClick={handleCut} className="flex items-center justify-center gap-1 w-20 py-1 bg-gray-50 border border-gray-400 rounded-sm shadow-sm hover:bg-gray-100 active:bg-gray-200 transition-colors text-sm text-gray-800 font-medium">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-scissors w-3.5 h-3.5" aria-hidden="true"><circle cx="6" cy="6" r="3"></circle><path d="M8.12 8.12 12 12"></path><path d="M20 4 8.12 15.88"></path><circle cx="6" cy="18" r="3"></circle><path d="M14.8 14.8 20 20"></path></svg> Cut
                                            </button>
                                            <button onClick={handleCopy} className="flex items-center justify-center gap-1 w-20 py-1 bg-gray-50 border border-gray-400 rounded-sm shadow-sm hover:bg-gray-100 active:bg-gray-200 transition-colors text-sm text-gray-800 font-medium">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-copy w-3.5 h-3.5" aria-hidden="true"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"></rect><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"></path></svg> Copy
                                            </button>
                                            <button onClick={handlePaste} className="flex items-center justify-center gap-1 w-20 py-1 bg-gray-50 border border-gray-400 rounded-sm shadow-sm hover:bg-gray-100 active:bg-gray-200 transition-colors text-sm text-gray-800 font-medium">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-clipboard w-3.5 h-3.5" aria-hidden="true"><rect width="8" height="4" x="8" y="2" rx="1" ry="1"></rect><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path></svg> Paste
                                            </button>
                                        </div>
                                        <div className={`font-semibold text-sm ${getFormScore(wordCount) === 2 ? 'text-green-600' :
                                            getFormScore(wordCount) === 1 ? 'text-orange-500' : 'text-red-500'
                                            }`}>
                                            Total Word Count: {wordCount}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>
                    ) : (currentQuestion.Questiontype === "Multiple Choice, Multiple Answers" || currentQuestion.Questiontype === "MCMA" || currentQuestion.Task === 2) ? (
                        /* Task 2: MCMA UI */
                        <ListeningMCMA
                            q={currentQuestion}
                            audioStatus={audioStatus}
                            timeLeft={timeLeft}
                            volume={volume}
                            setVolume={setVolume}
                            duration={duration}
                            currentTime={currentTime}
                            onAnswerChange={(ans) => {
                                setTextResponse(JSON.stringify(ans));
                                setWordCount(0);
                            }}
                        />

                    ) : (currentQuestion.Questiontype === "Multiple Choice, Single Answer" || currentQuestion.Questiontype === "MCSA" || currentQuestion.Task === 4) ? (
                        /* Task 4: MCSA UI */
                        <ListeningMCSA
                            q={currentQuestion}
                            audioStatus={audioStatus}
                            timeLeft={timeLeft}
                            volume={volume}
                            setVolume={setVolume}
                            duration={duration}
                            currentTime={currentTime}
                            currentQuestionIndex={currentQuestionIndex}
                            value={textResponse}
                            onAnswerChange={(ans) => {
                                setTextResponse(ans);
                                setWordCount(0);
                            }}
                        />

                    ) : (currentQuestion.Questiontype === "Select Missing Word" || currentQuestion.Questiontype === "SMW" || currentQuestion.Task === 5) ? (
                        /* Task 5: SMW UI */
                        <ListeningSMW
                            q={currentQuestion}
                            audioStatus={audioStatus}
                            timeLeft={timeLeft}
                            volume={volume}
                            setVolume={setVolume}
                            duration={duration}
                            currentTime={currentTime}
                            currentQuestionIndex={currentQuestionIndex}
                            value={textResponse}
                            onAnswerChange={(ans) => {
                                setTextResponse(ans);
                                setWordCount(0);
                            }}
                        />

                    ) : (currentQuestion.Questiontype === "Highlight Incorrect Words" || currentQuestion.Questiontype === "HIW" || currentQuestion.Task === 6) ? (
                        /* Task 6: HIW UI */
                        <ListeningHIW
                            q={currentQuestion}
                            audioStatus={audioStatus}
                            timeLeft={timeLeft}
                            volume={volume}
                            setVolume={setVolume}
                            duration={duration}
                            currentTime={currentTime}
                            currentQuestionIndex={currentQuestionIndex}
                            value={textResponse}
                            onAnswerChange={(ans) => {
                                setTextResponse(ans);
                                setWordCount(0);
                            }}
                        />

                    ) : (currentQuestion.Questiontype === "Write from Dictation" || currentQuestion.Questiontype === "WFD" || currentQuestion.Task === 7) ? (
                        /* Task 7: WFD UI */
                        <ListeningWFD
                            q={currentQuestion}
                            audioStatus={audioStatus}
                            timeLeft={timeLeft}
                            volume={volume}
                            setVolume={setVolume}
                            duration={duration}
                            currentTime={currentTime}
                            currentQuestionIndex={currentQuestionIndex}
                            value={textResponse}
                            wordCount={wordCount}
                            onAnswerChange={(ans) => {
                                setTextResponse(ans);
                                // Word count is handled internally but we can sync if needed
                            }}
                        />

                    ) : (
                        /* Fill in the Blanks UI */
                        <ListeningFillInTheBlank
                            key={currentQuestionIndex}
                            q={currentQuestion}
                            currentQuestionIndex={currentQuestionIndex}
                            audioStatus={audioStatus}
                            timeLeft={timeLeft}
                            volume={volume}
                            setVolume={setVolume}
                            duration={duration}
                            currentTime={currentTime}
                            answers={(() => {
                                try {
                                    return textResponse ? JSON.parse(textResponse) : [];
                                } catch (e) {
                                    return [];
                                }
                            })()}
                            onAnswerChange={(ans) => {
                                // For FIB, we might store as JSON string or array in textResponse for now
                                setTextResponse(JSON.stringify(ans));
                                setWordCount(0);
                            }}
                        />
                    )}
                </div>
                {/* Navigation - matches speaking/reading test */}
                <div className="ptest-header" style={{ marginTop: '20px' }}>
                    <div></div>
                    <div className="right-controls" style={{ display: 'flex', gap: '12px' }}>
                        <button
                            className="btn btn-ghost"
                            onClick={handlePrev}
                            disabled={currentQuestionIndex === 0}
                        >
                            ← Previous
                        </button>
                        <button
                            className="btn btn-primary"
                            onClick={handleNext}
                        >
                            Next →
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PteCoreListeningTest;
